import { useCallback, useMemo, useReducer } from 'react'
import { logger } from '@shared/lib/logger'
import { getFileExtension, getFileSizeBucket } from '@shared/lib/logger'
import { attempt, errorToLoggable } from '@shared/utils'
import { useAssetStore } from '../../app/providers/asset-provider'
import type { UploadedAsset, UploadEntry, UploadStatus, UseFileUploadOptions, UseFileUploadReturn } from '../types/file-upload.types'
import { ALLOWED_EXTENSIONS, MAX_FILE_SIZE_BYTES } from '../types/file-upload.types'

type Action =
  | { type: 'ADD'; entries: UploadEntry[] }
  | { type: 'PROGRESS'; id: string; pct: number }
  | { type: 'RESOLVE'; id: string; asset: UploadedAsset }
  | { type: 'ERROR'; id: string }
  | { type: 'REMOVE'; id: string }

function reducer(state: UploadEntry[], action: Action): UploadEntry[] {
  switch (action.type) {
    case 'ADD':
      return [...state, ...action.entries]

    case 'PROGRESS':
      return state.map((e) => (e.id === action.id ? { ...e, progress: action.pct } : e))

    case 'RESOLVE':
      return state.map((e) => (e.id === action.id ? { ...e, status: 'done', progress: 100, asset: action.asset } : e))

    // for now we'll just remove failed uploads
    case 'ERROR':
      return state.filter((e) => e.id !== action.id)

    case 'REMOVE':
      return state.filter((e) => e.id !== action.id)

    default:
      return state
  }
}

function buildInitialState(initialAssets?: UploadedAsset[]): UploadEntry[] {
  if (!initialAssets?.length) return []
  return initialAssets.map((asset) => ({
    id: asset.id,
    name: asset.name,
    status: 'done' as UploadStatus,
    progress: 100,
    asset,
  }))
}

function getExtension(filename: string): string {
  const dotIndex = filename.lastIndexOf('.')
  return dotIndex === -1 ? '' : filename.slice(dotIndex).toLowerCase()
}

function filterValidFiles(files: File[], remainingSlots: number): File[] {
  return files
    .filter((file) => file.size <= MAX_FILE_SIZE_BYTES && ALLOWED_EXTENSIONS.includes(getExtension(file.name) as (typeof ALLOWED_EXTENSIONS)[number]))
    .slice(0, remainingSlots)
}

export function useFileUpload({ maxFiles = 4, initialAssets }: UseFileUploadOptions = {}): UseFileUploadReturn {
  const { uploadAsset, deleteAssetById } = useAssetStore()

  const [entries, dispatch] = useReducer(reducer, undefined, () => buildInitialState(initialAssets))

  const addFiles = useCallback(
    (files: File[]) => {
      const remaining = maxFiles - entries.length
      if (remaining <= 0) {
        logger.warn('File upload skipped because max files limit was reached', {
          action: 'file_upload_limit_reached',
          component: 'useFileUpload',
          maxFiles,
          currentFileCount: entries.length,
        })

        return
      }

      logger.info('File selection received', {
        action: 'file_upload_selection_received',
        component: 'useFileUpload',
        selectedFileCount: files.length,
        remainingSlots: remaining,
      })

      files.forEach((file) => {
        const extension = getFileExtension(file.name)
        const isAllowedExtension = ALLOWED_EXTENSIONS.includes(extension as (typeof ALLOWED_EXTENSIONS)[number])
        const isAllowedSize = file.size <= MAX_FILE_SIZE_BYTES

        if (!isAllowedExtension || !isAllowedSize) {
          logger.warn('File rejected before upload', {
            action: 'file_upload_rejected',
            component: 'useFileUpload',
            fileExtension: extension,
            fileSizeBucket: getFileSizeBucket(file.size),
            fileSizeBytes: file.size,
            reason: !isAllowedExtension ? 'invalid_extension' : 'file_too_large',
          })
        }
      })

      const toUpload = filterValidFiles(files, remaining)
      if (toUpload.length === 0) {
        logger.warn('No valid files available to upload', {
          action: 'file_upload_no_valid_files',
          component: 'useFileUpload',
          selectedFileCount: files.length,
        })

        return
      }

      const newEntries: UploadEntry[] = toUpload.map((file) => ({
        id: crypto.randomUUID(),
        name: file.name,
        status: 'uploading' as UploadStatus,
        progress: 0,
      }))

      dispatch({ type: 'ADD', entries: newEntries })

      logger.info('Files queued for upload', {
        action: 'file_upload_queued',
        component: 'useFileUpload',
        acceptedFileCount: newEntries.length,
        rejectedFileCount: files.length - newEntries.length,
      })

      newEntries.forEach((entry, idx) => {
        const file = toUpload[idx] as File

        logger.info('Single file upload started', {
          action: 'file_upload_started',
          component: 'useFileUpload',
          uploadEntryId: entry.id,
          fileExtension: getFileExtension(file.name),
          fileSizeBucket: getFileSizeBucket(file.size),
          fileSizeBytes: file.size,
        })

        const runUpload = async (): Promise<void> => {
          const { data: asset, error } = await attempt(
            uploadAsset(file, (pct) => {
              dispatch({ type: 'PROGRESS', id: entry.id, pct })
            }),
          )

          if (error) {
            dispatch({ type: 'ERROR', id: entry.id })

            logger.error(
              'Single file upload failed',
              {
                action: 'file_upload_failed',
                component: 'useFileUpload',
                uploadEntryId: entry.id,
                fileExtension: getFileExtension(file.name),
                fileSizeBucket: getFileSizeBucket(file.size),
              },
              errorToLoggable(error),
            )
            return
          }

          dispatch({ type: 'RESOLVE', id: entry.id, asset })

          logger.info('Single file upload resolved', {
            action: 'file_upload_succeeded',
            component: 'useFileUpload',
            uploadEntryId: entry.id,
            assetId: asset.id,
          })
        }

        void runUpload()
      })
    },
    [entries.length, maxFiles, uploadAsset],
  )

  const removeEntry = useCallback(
    (id: string) => {
      const entry = entries.find((e) => e.id === id)
      dispatch({ type: 'REMOVE', id })

      if (entry?.asset?.id) {
        const assetId = entry.asset.id

        const runDelete = async (): Promise<void> => {
          const { error } = await attempt(deleteAssetById(assetId))

          if (error) {
            logger.error(
              'Failed to delete uploaded asset',
              {
                action: 'file_upload_asset_delete_failed',
                component: 'useFileUpload',
                uploadEntryId: id,
                assetId,
              },
              errorToLoggable(error),
            )
          }
        }

        void runDelete()
      }
    },
    [entries, deleteAssetById],
  )

  const isUploading = useMemo(() => entries.some((e) => e.status === 'uploading'), [entries])

  const uploadedAssets = useMemo(() => entries.filter((e) => e.status === 'done' && e.asset != null).map((e) => e.asset as UploadedAsset), [entries])

  return { entries, isUploading, uploadedAssets, addFiles, removeEntry }
}
